import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { apiClient } from "../apiClient";

// 🚀 AGGIORNATO: Incluso il canale "startup" nei tipi supportati
export type LeadType = "linkedin" | "small" | "startup" | "general";

interface Performance {
  associato: string;
  totale_contatti: number;
  positivi: number;
  negativi: number;
  in_attesa: number;
  disponibilita: number;
  perc_positivi: number;
}

interface OverviewContextProps {
  tipo: LeadType;
  setTipo: (tipo: LeadType) => void;
  totaleContatti: number | null;
  performance: Performance[];
  loadingPerf: boolean;
  conversione: string;
  disponibili: string;
  topPerformer?: string;
  fetchAll: (tipo: LeadType) => void;
}

const OverviewContext = createContext<OverviewContextProps | undefined>(undefined);

export const OverviewProvider = ({ children }: { children: ReactNode }) => {
  const [tipo, setTipo] = useState<LeadType>("linkedin");
  const [totaleContatti, setTotaleContatti] = useState<number | null>(null);
  const [performance, setPerformance] = useState<Performance[]>([]);
  const [loadingPerf, setLoadingPerf] = useState(true);

  // Calcola conversione in modo sicuro
  const conversione =
    totaleContatti && totaleContatti > 0 && performance.length > 0
      ? ((performance.reduce((sum, p) => sum + (p.positivi || 0), 0) / totaleContatti) * 100).toFixed(1) + "%"
      : "...";

  // Calcola disponibili in modo sicuro (restituendo il conteggio numerico o la percentuale a seconda della UI)
  const disponibili =
    totaleContatti && totaleContatti > 0 && performance.length > 0
      ? performance.reduce((sum, p) => sum + (p.disponibilita || 0), 0).toString()
      : "...";

  // Trova il Top Performer evitando crash se l'array è vuoto o contiene dati parziali
  const topPerformer =
    performance.length > 0
      ? performance.reduce((best, p) =>
          (p.perc_positivi || 0) > (best.perc_positivi || 0) ? p : best
        ).associato || "Nessuno"
      : "...";

  // Fetch totale contatti
  const fetchTotaleContatti = async (currentTipo: LeadType) => {
    try {
      const res = await apiClient.get(`contatti/count/?tipo=${currentTipo}`);
      setTotaleContatti(res.data.totale_contatti);
    } catch (err) {
      console.error("Errore fetch totale contatti:", err);
    }
  };

  // Fetch performance
  const fetchPerformance = async (currentTipo: LeadType) => {
    try {
      setLoadingPerf(true);
      const res = await apiClient.get(`contatti/performance/?tipo=${currentTipo}`);

      const rawPerformance = res.data.performance || [];
      const dataWithPerc: Performance[] = rawPerformance.map((item: any) => {
        const tot = item.totale_contatti || 0;
        return {
          ...item,
          totale_contatti: tot,
          positivi: item.positivi || 0,
          negativi: item.negativi || 0,
          in_attesa: item.in_attesa || 0,
          disponibilita: item.disponibilita || 0,
          perc_positivi: tot > 0 ? ((item.positivi || 0) / tot) * 100 : 0,
        };
      });

      dataWithPerc.sort((a, b) => b.perc_positivi - a.perc_positivi);
      setPerformance(dataWithPerc);
    } catch (err) {
      console.error("Errore fetch performance:", err);
      setPerformance([]);
    } finally {
      setLoadingPerf(false);
    }
  };

  // Fetch tutto
  const fetchAll = (currentTipo: LeadType) => {
    fetchTotaleContatti(currentTipo);
    fetchPerformance(currentTipo);
  };

  // Aggiorna automaticamente quando cambia il tipo
  useEffect(() => {
    fetchAll(tipo);
  }, [tipo]);

  return (
    <OverviewContext.Provider
      value={{ tipo, setTipo, totaleContatti, performance, loadingPerf, conversione, disponibili, topPerformer, fetchAll }}
    >
      {children}
    </OverviewContext.Provider>
  );
};

// Hook per usare il context
export const useOverview = () => {
  const context = useContext(OverviewContext);
  if (!context) throw new Error("useOverview deve essere usato all'interno di OverviewProvider");
  return context;
};